import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import SingleRow from './SingleRow';
import { Report, ReportStatus } from '../../../utils/graphql/generatedTS/graphql';
import { useParams } from 'react-router-dom';
import { useGetDashboardReportList } from '../../../hook/useGetDashboardReportList';
import { NavigateBeforeRounded, NavigateNextRounded } from '@mui/icons-material';
import GradientCircularProgress from "./GradientCircularProgress";

interface FilterProps {
  startDate?: number;
  endDate?: number;
  campaignName?: string;
  status?: ReportStatus;
}

export interface IDashboardReportTable {
  filters: FilterProps;
}

const DashboardReportTable: React.FC<IDashboardReportTable> = ({ filters }) => {
  const [retrieveError, setRetrieveError] = useState<string>('');
  const { idTeam } = useParams();

  // Function to build filter object
  const buildFilterObject = (filters: FilterProps, teamId: string) => {
    const filterObj = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== undefined)
    );

    return {
      idTeam: teamId,
      ...(Object.keys(filterObj).length > 0 ? { filter: filterObj } : {}),
    };
  };

  // Fetch report data
  const {
    reportList,
    loading,
    error,
    loadNext,
    loadPrevious,
    canLoadPrevious,
    canLoadNext
  } = useGetDashboardReportList(buildFilterObject(filters, idTeam || ''));

  // Set error message when an error occurs
  useEffect(() => {
    if (error) {
      setRetrieveError(error.message || 'An unexpected error occurred.');
    }
  }, [error]);

  // Guard clause if no team ID
  if (!idTeam) {
    return <Alert severity="error">Team ID is missing. Please try again.</Alert>;
  }

  return (
      <>
        {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" mt={4}>
              <GradientCircularProgress />
            </Box>
        ) : retrieveError ? (
            <Box mt={2}>
              <Alert severity="error">{retrieveError}</Alert>
            </Box>
        ) : (
            <>
              <TableContainer>
                <Table aria-label="collapsible table">
                  <TableHead style={{ backgroundColor: '#eaeaea', height: '50px', borderBottom: '6px solid #f8f9fa' }}>
                    <TableRow>
                      <TableCell />
                      <TableCell style={{ fontSize: '14px', fontWeight: 'bold', color: '#324158' }}>
                        Campaign's Name
                      </TableCell>
                      <TableCell style={{ fontSize: '14px', fontWeight: 'bold', color: '#324158' }}>
                        Last Run Date
                      </TableCell>
                      <TableCell style={{ fontSize: '14px', fontWeight: 'bold', color: '#324158' }}>
                        User
                      </TableCell>
                      <TableCell style={{ fontSize: '14px', fontWeight: 'bold', color: '#324158' }}>
                        Status
                      </TableCell>
                      <TableCell style={{ fontSize: '14px', fontWeight: 'bold', color: '#324158' }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportList && reportList.length > 0 ? (
                        reportList.map((report) =>
                            report?.idReport ? <SingleRow key={report.idReport} report={report as Report} /> : null
                        )
                    ) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No reports found.
                          </TableCell>
                        </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box display="flex" justifyContent="flex-end" mt={4}>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<NavigateBeforeRounded />}
                    onClick={loadPrevious}
                    disabled={!canLoadPrevious}
                />
                <Button
                    variant="contained"
                    size="small"
                    endIcon={<NavigateNextRounded />}
                    sx={{ ml: 1 }}
                    onClick={loadNext}
                    disabled={!canLoadNext}
                />
              </Box>
            </>
        )}
      </>
  );
};

export default DashboardReportTable;
